import { Checkbox, Shimmer } from "@fluentui/react";
import React, { useEffect, useRef, useState } from "react";
import { useGetGridConfigurationQuery } from "../../store/Api";
import { GridSelectionMode, IColumnDetails } from "../../models/GridConfiguration";
import CurrencyDisplay from "../Currency/CurrencyDisplay";
import ToggleField from "../Common/ToggleField";
import GridHeader from "../GridComposer/GridHeader";

interface IDataGridProps {
    enableShimmer?: boolean;
    items: any[],
    selectionMode: GridSelectionMode,
    gridSource?: string,
    gridId?: string,
    selections?: number[],
    pageSize?: number,
    loading: boolean,
    onFilterChange?: (filter?: string, filterField?: string) => void,
    onSortChange?: (sortBy?: string, sortDirection?: string) => void,
    onSelect?: (selection: any) => void,
    canResize?: boolean
    isCustomizeModal?: boolean,
    sortBy?: string,
    sortDirection?: string,
    filters?: { [key: string]: string },
    onFiltersChange?: (filters: { [key: string]: string }) => void,
    filter?: string,
    filterField?: string,
    stickyHeader?: boolean,
    useContentHeight?: boolean,

    canDrag?: boolean,
    overrideColumns?: IColumnDetails[],
    onColumnChange?: (column: IColumnDetails[]) => void,

    getItemId: (item: any) => string;
    selectedItems?: string[];
    onSelectionChange?: (selectedItems: any[]) => void;

    draggableRows?: boolean;
    onRowReorder?: (fromIndex: number, toIndex: number) => void;
    /** Extra class on the grid's root, for per-screen styling (see .bundle-items-grid in App.css). */
    className?: string;
}

const DataGrid: React.FC<IDataGridProps> = (props) => {
    const [highlightedRow, setHighlightedRow] = useState<number>();
    const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

    const [filter, setFilter] = React.useState('');
    const [filterField, setFilterField] = React.useState('');
    const [sortField, setSortField] = React.useState('');
    const [sortDirection, setSortDirection] = React.useState('');
    const [selectedRows, setSelectedRows] = React.useState<number[]>([]);
    const [multiSelectKeys, setMultiSelectKeys] = React.useState<boolean>(false);
    const [columnDetails, setColumnDetails] = React.useState<IColumnDetails[]>([])
    const [columnKeys, setColumnKeys] = React.useState<string[]>([])
    const [tableHeight, setTableHeight] = useState('auto');
    const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
    const rootRef = useRef<HTMLDivElement>(null);
    const tableElement = useRef<HTMLTableElement>(null);
    const [globalChecked, setGlobalChecked] = useState(false);

    useEffect(() => {
        if (props.filterField) {
            setFilterField(props.filterField)
        }
        if (props.filter) {
            setFilter(props.filter)
        }
        if (props.sortBy) {
            setSortField(props.sortBy)
        }
        if (props.sortDirection) {
            setSortDirection(props.sortDirection)
        }
    }, [props.filter, props.filterField, props.sortBy, props.sortDirection]);

    useEffect(() => {
        const recalculate = () => {
            if (rootRef.current && tableElement.current && !props.useContentHeight) {
                const rect = rootRef.current.getBoundingClientRect();
                const top = tableElement.current.getBoundingClientRect().top;
                const height = window.innerHeight - top - 50;
                console.log('Recalculating table height', height);
                setTableHeight(`${height}px`);
            }
            else {
                setTableHeight('auto');
            }
        };
        // Define handlers so they can be removed later
        const handleResize = () => recalculate();
        const handleDrag = () => recalculate();
        recalculate()
        window.addEventListener('resize', handleResize);
        window.addEventListener('drag', handleDrag);

        // Cleanup function to remove event listeners on unmount
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('drag', handleDrag);
        };
    }, []);

    //#region Keyboard navigation and selection
    useEffect(() => {
        if (props.selectedItems && props.selectedItems.length === props.items.length) {
            setGlobalChecked(true);
        } else {
            setGlobalChecked(false);
        }
    }, [props.selectedItems, props.items])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                setHighlightedRow(undefined);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [rootRef]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.code === "Space" && highlightedRow !== undefined && highlightedRow !== null) {
                event.preventDefault();
                const item = props.items[highlightedRow];
                if (!item) return;
                const itemId = props.getItemId(item);
                if (props.selectionMode === GridSelectionMode.multiple) {
                    if (props.selectedItems?.includes(itemId)) {
                        props.onSelectionChange?.((props.selectedItems ?? []).filter(id => id !== itemId));
                    } else {
                        props.onSelectionChange?.([...(props.selectedItems ?? []), itemId]);
                    }
                }
                if (props.selectionMode === GridSelectionMode.single) {
                    if (props.selectedItems?.includes(itemId)) {
                        props.onSelectionChange?.([]);
                    } else {
                        props.onSelectionChange?.([itemId]);
                    }
                }
            } else if (event.code === "ArrowDown" && highlightedRow !== undefined && highlightedRow < props.items.length - 1) {
                event.preventDefault();
                const nextIndex = Math.min(props.items.length - 1, highlightedRow + 1);
                setHighlightedRow(nextIndex);
            } else if (event.code === "ArrowUp" && highlightedRow !== undefined && highlightedRow > 0) {
                event.preventDefault();
                const prevIndex = Math.max(0, highlightedRow - 1);
                setHighlightedRow(prevIndex);
            }
        };
        const current = rootRef.current;
        if (current) {
            current.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            if (current) {
                current.removeEventListener("keydown", handleKeyDown);
            }
        };
    }, [highlightedRow, props.items, props.selectedItems, props.selectionMode, props.getItemId, props.onSelectionChange]);

    useEffect(() => {
        if (
            highlightedRow !== undefined &&
            highlightedRow !== null &&
            rowRefs.current[highlightedRow]
        ) {
            rowRefs.current[highlightedRow]?.scrollIntoView({
                behavior: "smooth",
                block: 'nearest',
            });
        }
    }, [highlightedRow]);
    //#endregion

    //#region Drag & Drop Columns
    // indicates if the current column set is editable (not default)
    const [editableColumns, setEditableColumns] = useState<boolean>(false);
    // Improved drag-and-drop state for columns
    const [draggedColIndex, setDraggedColIndex] = useState<number | null>(null);
    // dropIndicator: { col: number, position: 'left' | 'right' } | null
    const [dropIndicator, setDropIndicator] = useState<{ col: number, position: 'left' | 'right' } | null>(null);

    const getGrid = useGetGridConfigurationQuery({ gridIdentifier: props.gridSource ?? "", gridIdHint: props.gridId ?? '' }, { skip: !props.gridSource || props.gridSource == '' || !props.gridId || props.gridId == '' });

    //#endregion

    //#region Drag & Drop Rows
    const [dragRowIndex, setDragRowIndex] = React.useState<number | null>(null);
    const [dropRowIndex, setDropRowIndex] = React.useState<number | null>(null);
    //#endregion




    const handleDragStart = (event: React.DragEvent<HTMLDivElement>, index: number) => {
        setDraggedColIndex(index);
        event.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (event: React.DragEvent<HTMLTableHeaderCellElement>, index: number, position: 'left' | 'right') => {
        event.preventDefault();
        if (draggedColIndex !== null && index !== draggedColIndex) {
            setDropIndicator({ col: index, position });
        }
    };

    const handleDragLeave = (event: React.DragEvent<HTMLTableHeaderCellElement>) => {
        event.preventDefault();
        setDropIndicator(null);
    };

    const handleDrop = (event: React.DragEvent<HTMLTableHeaderCellElement>, index: number, position: 'left' | 'right') => {
        event.preventDefault();
        if (draggedColIndex !== null && (index !== draggedColIndex || position === 'left')) {
            let targetIndex = index;
            if (position === 'right') targetIndex++;
            // Adjust for removing before inserting
            const updatedColumns = [...columnDetails];
            const [removed] = updatedColumns.splice(draggedColIndex, 1);
            // If dragging right and source is before target, targetIndex--
            if (draggedColIndex < targetIndex) targetIndex--;
            updatedColumns.splice(targetIndex, 0, removed);
            setColumnDetails(updatedColumns);
            props?.onColumnChange?.(updatedColumns);
        }
        setDraggedColIndex(null);
        setDropIndicator(null);
    };

    const handleDragEnd = () => {
        setDraggedColIndex(null);
        setDropIndicator(null);
    };
    useEffect(() => {
        setColumnKeys(getGrid.data?.visibleColumns ?? [])
    }, [getGrid.data]);

    useEffect(() => {
        const initialSet: IColumnDetails[] = []

        columnKeys.forEach((key) => {
            const found = getGrid.data?.columnDetails?.find(a => a.identifier === key)
            if (found) {
                initialSet.push(found)
            }
        })
        //If override columns are present and they are not marked as required, we check if there's an existing column with the same identifier, and replace it if so.
        //Otherwise, if it's not found we ignore it. If it is marked as required, it will always be added 
        if (props.overrideColumns) {
            props.overrideColumns.forEach((column,) => {
                const foundIndex = initialSet.findIndex(a => a.identifier == column.identifier)
                if (foundIndex != -1) {
                    initialSet[foundIndex] = column;
                }
                else if (column.index != undefined && (column.required || props.isCustomizeModal)) {
                    initialSet.splice(column.index, 0, column)
                }
                else if (column.required || props.isCustomizeModal) {
                    initialSet.push(column)
                }
            })
        }

        setColumnDetails(() => [...initialSet])

    }, [columnKeys, props.overrideColumns]);

    const getShimmer = () => {
        const shimmerRows = [];
        // Fall back to full-width when expecting server-side columns that haven't loaded yet.
        // columnKeys comes from getGrid.data, so it's empty until the grid config resolves.
        const useFullWidth = columnDetails.length === 0 || (!!props.gridSource && columnKeys.length === 0);
        for (let i = 0; i < (props?.pageSize ?? 25); i++) {
            shimmerRows.push(<tr key={`shimmer-${i}${(props.isCustomizeModal ? '-customize' : '')}`} className={'composer-row'}>
                {useFullWidth
                    ? <td className={'composer-cell'} style={{ width: '100%' }}><Shimmer /></td>
                    : columnDetails.map((column) => {
                        return <td key={`shimmer-${i}-${column.identifier}${(props.isCustomizeModal ? '-customize' : '')}`} style={{ minWidth: column.minimumWidth }}
                            className={'composer-cell'}><Shimmer /></td>
                    })
                }
            </tr>)
        }
        return shimmerRows
    }

    //Add any new column types here
    const getTypedValue = (column: IColumnDetails, index: number, value?: string) => {
        if (column.component) {
            return <div onClick={() => column.onClick?.(props.items[index])}
                onBlur={() => column.onBlur?.(props.items[index], value ?? '')}>{column.component(props.items[index])}</div>
        }
        if (column.type === 'Number') {
            return <div>{value ? value.toLocaleString() : '-'}</div>
        }
        if (column.type === 'DateTime') {
            return <div>{value ? new Date(value).toLocaleDateString() : '-'}</div>
        }
        if (column.type === 'Boolean') {
            return <ToggleField disabled={true} checked={value?.toLowerCase() == 'true'} width={column.effectiveWidth} />
        }
        if (column.type === 'Currency') {
            return <CurrencyDisplay value={value} />
        }
        if (column.type === 'Image' || column.type === 'Thumbnail') {
            return value ? <img src={value} alt={column.label} style={{ maxHeight: '30px', maxWidth: column.effectiveWidth ? `${column.effectiveWidth - 10}px` : '100px', objectFit: 'contain' }} /> : '-'
        }
        return <>{value}</>
    }

    const getRowClassName = (index: number) => {
        if (selectedRows.includes(index)) {
            return 'composer-row-selected'
        }
        return 'composer-row'
    }

    document.addEventListener('keydown', (event) => {
        if (event.key == 'Control') {
            event.preventDefault();
            setMultiSelectKeys(true)
        }
    });
    document.addEventListener('keyup', (event) => {
        if (event.key == 'Control') {
            setMultiSelectKeys(false)
        }
    });

    const handleRowSelectionClick = (index: number) => {
        setHighlightedRow(index);
    }

    const handleSelectAll = () => {
        if (props.selectionMode != GridSelectionMode.multiple) return
        if (selectedRows.length === props.items.length) {
            setSelectedRows([])
        } else {
            setSelectedRows(props.items.map((item, index) => index))
        }
    }

    const handleSelectorClick = (ev: React.MouseEvent<HTMLDivElement>, index: number) => {
        ev.stopPropagation();
        if (props.selectionMode === GridSelectionMode.none) return
        if (props.selectionMode === GridSelectionMode.single) {
            setSelectedRows([index])
        }
        if (selectedRows.includes(index)) {
            setSelectedRows(selectedRows.filter((r) => r !== index))
        } else {
            setSelectedRows((sr) => [...sr, index])
        }

    }

    const allSelected = () => {
        return props.items.length === selectedRows.length
    }

    const mouseDown = (index: number) => {
        setActiveIndex(index);
    }

    const handleGlobalCheckboxChange = () => {
        // Because we want the next state to be checked if indeterminate, we ignore the current state and just use the new checked value
        console.log('Global checkbox changed. Current selected rows:', selectedRows, 'Total items:', props.items.length);
        if (!globalChecked) {
            console.log('Selecting all items');
            props.onSelectionChange?.(props.items.map(item => props.getItemId(item)));
        } else {
            console.log('Deselecting all items');
            props.onSelectionChange?.([]);
        }
    }

    return (
        <div ref={rootRef} className={props.className}>
            <div tabIndex={0} style={{ overflow: 'auto', height: tableHeight }}>
                <table width={'100%'} style={{ borderSpacing: '0', overflow: 'auto' }} ref={tableElement}>
                    <thead className={`composer-header-main ${props.stickyHeader ? 'sticky-header' : ''}`}>
                        <tr>
                            {props.selectionMode === GridSelectionMode.multiple && (
                                <th style={{ width: '50px' }} className='composer-header'>
                                    <Checkbox
                                        styles={{
                                            checkbox: {
                                                margin: '0 auto',
                                                display: props.selectedItems && props.selectedItems.length > 0 ? undefined : 'none'
                                            },
                                            root: {
                                                display: 'relative'
                                            }
                                        }}
                                        checked={globalChecked}
                                        indeterminate={props.selectedItems ? props.selectedItems.length > 0 && props.selectedItems.length < props.items.length : false}
                                        onChange={handleGlobalCheckboxChange}
                                    />
                                </th>
                            )}
                            {props.selectionMode === GridSelectionMode.single && (
                                <th style={{ width: '50px' }} className='composer-header'></th>
                            )}
                            {columnDetails.length > 0 && columnDetails.map((column, index) => {
                                // For each column, render header and attach drag events
                                const isDragged = draggedColIndex === index;
                                // Determine if drop indicator should be shown on this cell
                                const showDropLeft = dropIndicator && draggedColIndex !== null && dropIndicator.col === index && dropIndicator.position === 'left' && draggedColIndex !== index;
                                const showDropRight = dropIndicator && draggedColIndex !== null && dropIndicator.col === index && dropIndicator.position === 'right' && draggedColIndex !== index;
                                return (
                                    <th
                                        className={'composer-header'}
                                        key={`grid-header-${props.gridSource}-${column.identifier}-${index}${(props.isCustomizeModal ? '-customize' : '')}`}
                                        draggable={!!props.canDrag}
                                        onDragStart={e => handleDragStart(e, index)}
                                        onDragEnd={handleDragEnd}
                                        onDragOver={e => {
                                            // Determine if mouse is in left or right half
                                            const rect = (e.target as HTMLElement).getBoundingClientRect();
                                            const x = e.clientX - rect.left;
                                            const position = x < rect.width / 2 ? 'left' : 'right';
                                            handleDragOver(e, index, position);
                                        }}
                                        onDrop={e => {
                                            const rect = (e.target as HTMLElement).getBoundingClientRect();
                                            const x = e.clientX - rect.left;
                                            const position = x < rect.width / 2 ? 'left' : 'right';
                                            handleDrop(e, index, position);
                                        }}
                                        onDragLeave={handleDragLeave}
                                        style={isDragged ? { opacity: 0.5 } : {}}
                                    >
                                        {/* Inline drop indicator */}
                                        {showDropLeft && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: '4px',
                                                    background: '#7AC143',
                                                    borderRadius: 2,
                                                    zIndex: 100,
                                                    height: '100%',
                                                    marginLeft: '-2px',
                                                    boxShadow: '0 0 2px #7AC143',
                                                }}
                                            />
                                        )}
                                        {showDropRight && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    right: 0,
                                                    top: 0,
                                                    bottom: 0,
                                                    width: '4px',
                                                    background: '#7AC143',
                                                    borderRadius: 2,
                                                    zIndex: 100,
                                                    height: '100%',
                                                    marginRight: '-2px',
                                                    boxShadow: '0 0 2px #7AC143',
                                                }}
                                            />
                                        )}
                                        <GridHeader
                                            field={column.identifier.replace('.keyword', '')}
                                            label={column.label}
                                            tooltip={column.tooltip}
                                            sortable={column.sortable}
                                            filterable={column.filterable}
                                            filterValue={filter}
                                            filteredField={filterField}
                                            filters={props.filters}
                                            filterOptions={column?.customFilterOptions?.()}
                                            filterField={column?.filterField}
                                            customFilterComponent={column?.customFilterComponent}
                                            onFilterChange={props.onFilterChange}
                                            onFiltersChange={props.onFiltersChange}
                                            onSort={(c, s) => {console.log('sort');props.onSortChange?.(c, s)}}
                                            sortDirection={sortDirection}
                                            sortField={sortField}
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {props.loading ?
                            <React.Fragment>{getShimmer()}</React.Fragment> :
                            <React.Fragment>
                                {props.items.length === 0 && !props.enableShimmer ?
                                    <tr className={'composer-row'} style={{ height: '100px' }}>
                                        <td colSpan={columnDetails?.length ?? 1} style={{ textAlign: 'center' }}>
                                            <span>No items found</span>
                                        </td>
                                    </tr> :
                                    <React.Fragment>
                                        {props.items.map((item, itemIndex) => {
                                            return (
                                                <tr
                                                    id={`datagrid-row-${props.getItemId(item)}-${itemIndex}`}
                                                    key={`datagrid-row-${props.getItemId(item)}-${itemIndex}${(props.isCustomizeModal ? '-customize' : '')}`}
                                                    className={highlightedRow === itemIndex ? 'composer-row-selected' : 'composer-row'}
                                                    ref={el => rowRefs.current[itemIndex] = el}
                                                    onClick={() => { handleRowSelectionClick(itemIndex) }}
                                                    draggable={!!props.draggableRows}
                                                    onDragStart={props.draggableRows ? (e) => {
                                                        e.stopPropagation();
                                                        e.dataTransfer.effectAllowed = 'move';
                                                        setDragRowIndex(itemIndex);
                                                    } : undefined}
                                                    onDragOver={props.draggableRows ? (e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (dragRowIndex !== null && dragRowIndex !== itemIndex) setDropRowIndex(itemIndex);
                                                    } : undefined}
                                                    onDrop={props.draggableRows ? (e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (dragRowIndex !== null && dragRowIndex !== itemIndex) props.onRowReorder?.(dragRowIndex, itemIndex);
                                                        setDragRowIndex(null);
                                                        setDropRowIndex(null);
                                                    } : undefined}
                                                    onDragEnd={props.draggableRows ? () => {
                                                        setDragRowIndex(null);
                                                        setDropRowIndex(null);
                                                    } : undefined}
                                                    style={{
                                                        opacity: dragRowIndex === itemIndex ? 0.4 : 1,
                                                        borderTop: dropRowIndex === itemIndex && dragRowIndex !== itemIndex ? '2px solid rgb(120, 156, 47)' : undefined,
                                                        cursor: props.draggableRows ? 'grab' : undefined,
                                                    }}
                                                >
                                                    {props.selectionMode === GridSelectionMode.multiple && (
                                                        <td style={{ width: '50px', textAlign: 'center' }}>
                                                            <Checkbox
                                                                styles={{ checkbox: { margin: '0 auto' }, root: { display: 'relative' } }}
                                                                checked={props.selectedItems?.includes(props.getItemId(item))}
                                                                onChange={(ev, checked) => {
                                                                    if (checked) {
                                                                        props.onSelectionChange?.([...(props.selectedItems ?? []), props.getItemId(item)]);
                                                                    } else {
                                                                        props.onSelectionChange?.((props.selectedItems ?? []).filter(id => id !== props.getItemId(item)));
                                                                    }
                                                                }}
                                                            />
                                                        </td>
                                                    )}
                                                    {props.selectionMode === GridSelectionMode.single && (
                                                        <td style={{ width: '50px' }}>
                                                            <Checkbox
                                                                styles={{ checkbox: { margin: '0 auto' }, root: { display: 'relative' } }}
                                                                checked={props.selectedItems?.includes(props.getItemId(item))}
                                                                onChange={(ev, checked) => {
                                                                    if (checked) {
                                                                        props.onSelectionChange?.([props.getItemId(item)]);
                                                                    } else {
                                                                        props.onSelectionChange?.([]);
                                                                    }
                                                                }}
                                                            />
                                                        </td>
                                                    )}
                                                    {columnDetails?.map((column, index) => {
                                                        let workingValue;
                                                        if (props.isCustomizeModal) {
                                                            workingValue = item[column.identifier]
                                                        }
                                                        else {
                                                            //.keyword needs to be appended to the identifier to make sorting work with elastic for string fields.

                                                            const normalizedIdentifier = column.identifier.replace('.keyword', '')

                                                            const segmentedIdentifier = normalizedIdentifier.split('.')
                                                            workingValue = item[segmentedIdentifier[0]]
                                                            for (let i = 0; i < segmentedIdentifier.length; i++) {
                                                                if (!workingValue) break;
                                                                const key = segmentedIdentifier?.[i + 1]
                                                                if (!key) break;
                                                                workingValue = workingValue[segmentedIdentifier[i + 1]]
                                                            }
                                                        }
                                                        return (
                                                            <td className={'composer-cell'} 
                                                                key={`datagrid-cell-${props.getItemId(item)}-${itemIndex}-${column.identifier}${(props.isCustomizeModal ? '-customize' : '')}`} 
                                                                id={`datagrid-cell-${props.getItemId(item)}-${itemIndex}-${column.identifier}`} 
                                                                style={{
                                                                    minWidth: (column.minimumWidth ? `${column.minimumWidth}px` : '50px'), 
                                                                    width: column.effectiveWidth ? `${column.effectiveWidth}px` : 'auto'}}
                                                            >
                                                                <span>{getTypedValue(column, itemIndex, workingValue?.toString())}</span>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>}
                            </React.Fragment>}
                    </tbody>

                </table>
            </div>
        </div>)
}
export default DataGrid;